import { apiClient, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapResultsFileDto } from '@/shared/data/dtoMappers';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  ResultsFileDto,
  ResultsFileSummaryDto,
} from '@flowqa/shared';

type ResultsFileQueryRequest = {
  page: number;
  pageSize: number;
  filters?: QueryFilter[];
  sorts?: QuerySort[];
  search?: string;
  includeDeleted?: boolean;
  includeSummary?: boolean;
  includeSummaryDetails?: boolean;
  include?: ResultsFileInclude;
};

const resultsFileSortFieldMap: Readonly<Record<string, string>> = {
  updatedAt: 'createdAt',
};

const mapResultsFileSorts = (sorts: QuerySort[]): QuerySort[] => {
  const mappedSorts: QuerySort[] = [];
  for (const sort of sorts) {
    const field = typeof sort.field === 'string' ? sort.field : String(sort.field);
    const direction = sort.direction === 'desc' ? 'desc' : 'asc';
    const mappedField = Object.prototype.hasOwnProperty.call(resultsFileSortFieldMap, field)
      ? resultsFileSortFieldMap[field]
      : field;
    mappedSorts.push({ field: mappedField, direction });
  }
  return mappedSorts;
};

export type ResultsFileInclude = {
  project?: boolean;
  createdBy?: boolean;
  createdByEmail?: boolean;
};
export type ResultsFileUpdateInput = {
  slug: string;
  tags?: string[];
};

export const getResultsFileBySlug = async (
  resultsFileSlug: string,
  orgId?: string | null,
): Promise<ResultsFileDto> =>
  mapResultsFileDto(
    await apiClient.get<ResultsFileDto>(`results-files/${resultsFileSlug}`, { orgId }),
  );

export const queryResultsFiles = async (
  input: {
    page: number;
    pageSize: number;
    filters?: QueryFilter[];
    sorts?: QuerySort[];
    search?: string;
    includeDeleted?: boolean;
    includeSummary?: boolean;
    includeSummaryDetails?: boolean;
    include?: ResultsFileInclude;
  },
  orgId?: string | null,
): Promise<QueryResult<ResultsFileDto>> => {
  const includeSummary = input.includeSummary;
  const includeSummaryDetails = input.includeSummaryDetails;
  const includeDeleted = input.includeDeleted;
  const include = input.include;
  const payload: ResultsFileQueryRequest = {
    page: input.page,
    pageSize: input.pageSize,
    filters: input.filters,
    sorts: input.sorts ? mapResultsFileSorts(input.sorts) : undefined,
    search: input.search,
    includeDeleted,
    includeSummary,
    includeSummaryDetails,
    include,
  };
  const result = await apiClient.post<QueryResult<ResultsFileDto>>('results-files/query', payload, {
    orgId,
  });
  return mapQueryResult(result, mapResultsFileDto);
};

export const queryResultsFileSummary = async (
  input: ResultsFileQueryRequest,
  orgId?: string | null,
): Promise<ResultsFileSummaryDto> =>
  await apiClient.post<ResultsFileSummaryDto>(
    'results-files/summary',
    {
      ...input,
      sorts: input.sorts ? mapResultsFileSorts(input.sorts) : undefined,
    },
    { orgId },
  );

export const deleteResultsFile = async (
  input: { slug: string },
  organisationId?: string | null,
): Promise<{ slug: string }> => {
  await apiClient.post<undefined>(`results-files/${input.slug}/delete`, undefined, {
    orgId: organisationId ?? undefined,
  });
  return { slug: input.slug };
};

export const restoreResultsFile = async (
  input: { slug: string },
  organisationId?: string | null,
): Promise<{ slug: string }> => {
  await apiClient.post<undefined>(`results-files/${input.slug}/restore`, undefined, {
    orgId: organisationId ?? undefined,
  });
  return { slug: input.slug };
};

export const updateResultsFile = async (
  input: ResultsFileUpdateInput,
  organisationId?: string | null,
): Promise<ResultsFileDto> => {
  const token = getAuthToken();
  return mapResultsFileDto(
    await apiClient.patch<ResultsFileDto>(
      `results-files/${input.slug}`,
      {
        tags: input.tags,
      },
      {
        orgId: organisationId ?? undefined,
        token,
      },
    ),
  );
};

export const updateResultsFileTags = async (
  input: ResultsFileUpdateInput,
  organisationId?: string | null,
): Promise<ResultsFileDto> => await updateResultsFile(input, organisationId);
